import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// NotificationsBell — bell icon in the nav with a dropdown of recent
// notifications. State comes from two sources:
//
//   1. service-worker postMessage events. When a push lands, sw.js fans
//      a {type:'notification', title, body, url, tag, ts} message to all
//      open SPA tabs. The bell appends it.
//   2. localStorage. The same list is persisted across reloads so the
//      bell carries history even after a refresh.
//
// "Unread" is tracked via a lastReadTs in localStorage — anything with
// ts > lastReadTs counts as unread. Clicking the bell or a notification
// resets lastReadTs to now.
//
// Limitations (v1):
//   * Only catches notifications that arrived while the SPA was loaded
//     by some open tab. A push that fires while every tab is closed is
//     visible as an OS toast but not in the bell.
//   * No SSE backchannel yet; if browser push permission is denied,
//     no in-app notifications surface either. Push-fed SSE is a sensible
//     follow-up if that becomes a real complaint.

const STORAGE_KEY = "animus.notifications";
const READ_KEY = "animus.notifications.lastReadTs";
const MAX_ITEMS = 30;

type NotifMsg = {
  type: "notification";
  title: string;
  body?: string;
  url?: string;
  tag?: string | null;
  ts: number;
};

export type NotifItem = {
  id: string;
  title: string;
  body?: string;
  url?: string;
  ts: number;
};

function loadItems(): NotifItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((x) => typeof x?.ts === "number" && typeof x?.title === "string");
  } catch {
    return [];
  }
}

function saveItems(items: NotifItem[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS))); } catch {/* quota */}
}

function loadLastRead(): number {
  try {
    const n = Number(localStorage.getItem(READ_KEY) ?? 0);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function saveLastRead(ts: number) {
  try { localStorage.setItem(READ_KEY, String(ts)); } catch {/* quota */}
}

function timeAgo(ts: number): string {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return Math.floor(sec / 60) + "m ago";
  if (sec < 86400) return Math.floor(sec / 3600) + "h ago";
  return Math.floor(sec / 86400) + "d ago";
}

export function NotificationsBell() {
  const [items, setItems] = useState<NotifItem[]>(() => loadItems());
  const [lastRead, setLastRead] = useState<number>(() => loadLastRead());
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);

  // Listen for SW push fan-outs.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (event: MessageEvent) => {
      const d = event.data as NotifMsg | undefined;
      if (!d || d.type !== "notification") return;
      const item: NotifItem = {
        id: `${d.ts}-${d.tag ?? d.title}-${Math.random().toString(36).slice(2, 6)}`,
        title: d.title,
        body: d.body,
        url: d.url,
        ts: d.ts || Date.now(),
      };
      setItems((cur) => {
        // If a tag is supplied and matches an existing item, replace it
        // (mirrors the SW's renotify-by-tag semantics).
        let next = cur;
        if (d.tag) {
          next = cur.filter((x) => x.id.indexOf(`-${d.tag}-`) < 0);
        }
        const out = [item, ...next].slice(0, MAX_ITEMS);
        saveItems(out);
        return out;
      });
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, []);

  // Close dropdown when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const unread = items.filter((x) => x.ts > lastRead).length;

  const openDropdown = () => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        const now = Date.now();
        setLastRead(now);
        saveLastRead(now);
      }
      return next;
    });
  };

  const onItemClick = (n: NotifItem) => {
    setOpen(false);
    if (n.url) {
      // SPA links use the basename="/panel" router. Strip the prefix if
      // present and route internally, otherwise fall back to window.location
      // for external URLs.
      if (n.url.startsWith("/panel/")) {
        navigate(n.url.replace(/^\/panel/, "") || "/");
      } else if (n.url.startsWith("/")) {
        navigate(n.url);
      } else {
        window.location.href = n.url;
      }
    }
  };

  const clearAll = () => {
    setItems([]);
    saveItems([]);
    const now = Date.now();
    setLastRead(now);
    saveLastRead(now);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={openDropdown}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
        aria-expanded={open}
        className="relative rounded p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
        </svg>
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-4 text-center"
            aria-hidden="true"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700">
            <span className="font-semibold text-sm">Notifications</span>
            {items.length > 0 && (
              <button
                onClick={clearAll}
                className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              >Clear</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                <p className="mb-1">No notifications yet.</p>
                <p className="text-xs">Enable push in Settings to receive fleet + device events.</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => onItemClick(n)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-0.5">
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{n.title}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 flex-shrink-0">{timeAgo(n.ts)}</span>
                      </div>
                      {n.body && (
                        <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">{n.body}</p>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
